import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Autocomplete,
  IconButton,
  Divider,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const PIPCreateForm = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    employeeId: '',
    performanceReviewId: '',
    pipStartDate: new Date().toISOString().split('T')[0],
    pipEndDate: '',
    pipDuration: 30,
    performanceIssues: [''],
    objectives: [{ objective: '', targetValue: '', targetDate: '' }],
    supportProvided: [''],
    reviewFrequency: 'BI_WEEKLY',
    managerComments: ''
  });

  const reviewFrequencyOptions = [
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'BI_WEEKLY', label: 'Bi-Weekly' },
    { value: 'MONTHLY', label: 'Monthly' }
  ];

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (formData.employeeId) {
      fetchEmployeeReviews(formData.employeeId);
    }
  }, [formData.employeeId]);

  useEffect(() => {
    if (formData.pipStartDate && formData.pipDuration) {
      const startDate = new Date(formData.pipStartDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + parseInt(formData.pipDuration));
      setFormData(prev => ({
        ...prev,
        pipEndDate: endDate.toISOString().split('T')[0]
      }));
    }
  }, [formData.pipStartDate, formData.pipDuration]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/employee/getAllEmployees');
      if (response.data) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchEmployeeReviews = async (employeeId) => {
    try {
      const response = await axios.post('/api/employee-performance/review/list', {
        employeeId: employeeId,
        page: 1,
        limit: 10
      });
      if (response.data.success) {
        setReviews(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleAddArrayField = (field) => {
    const newValue = field === 'objectives' 
      ? { objective: '', targetValue: '', targetDate: '' }
      : '';
    
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newValue]
    }));
  };

  const handleRemoveArrayField = (field, index) => {
    if (formData[field].length > 1) {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.employeeId) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }

    if (!formData.pipStartDate || !formData.pipEndDate) {
      Swal.fire('Error', 'Please select PIP start and end dates', 'error');
      return;
    }

    if (formData.performanceIssues.filter(i => i.trim()).length === 0) {
      Swal.fire('Error', 'Please add at least one performance issue', 'error');
      return;
    }

    if (formData.objectives.filter(o => o.objective.trim()).length === 0) {
      Swal.fire('Error', 'Please add at least one objective', 'error');
      return;
    }

    setLoading(true);

    try {
      const cleanedData = {
        ...formData,
        performanceIssues: formData.performanceIssues.filter(i => i.trim()),
        objectives: formData.objectives.filter(o => o.objective.trim()),
        supportProvided: formData.supportProvided.filter(s => s.trim())
      };

      const response = await axios.post('/api/employee-performance/pip/create', cleanedData);

      if (response.data.success) {
        Swal.fire({
          title: '✅ PIP Created Successfully!',
          icon: 'success',
          html: `
            <div style="text-align: left; padding: 20px;">
              <p><strong>Employee:</strong> ${employees.find(e => e._id === formData.employeeId)?.staff_name || 'N/A'}</p>
              <p><strong>Duration:</strong> ${formData.pipDuration} days</p>
              <p><strong>Start Date:</strong> ${new Date(formData.pipStartDate).toLocaleDateString()}</p>
              <p><strong>End Date:</strong> ${new Date(formData.pipEndDate).toLocaleDateString()}</p>
              <p><strong>Review Frequency:</strong> ${formData.reviewFrequency}</p>
              <hr style="margin: 15px 0;">
              <p style="color: #f44336;"><strong>⚠️ Note:</strong> Employee has been notified about the PIP.</p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: '📋 View All PIPs',
          cancelButtonText: 'Create Another',
          confirmButtonColor: '#1976d2',
          cancelButtonColor: '#757575'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/userdashboard/staff/hrm/performance/pip');
          } else if (result.dismiss === Swal.DismissReason.cancel) {
            // Reset form for creating another PIP
            setFormData({
              employeeId: '',
              performanceReviewId: '',
              pipStartDate: new Date().toISOString().split('T')[0],
              pipEndDate: '',
              pipDuration: 30,
              performanceIssues: [''],
              objectives: [{ objective: '', targetValue: '', targetDate: '' }],
              supportProvided: [''],
              reviewFrequency: 'BI_WEEKLY',
              managerComments: ''
            });
            setReviews([]);
          }
        });
      }
    } catch (error) {
      console.error('Error creating PIP:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to create PIP', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/userdashboard/staff/hrm/performance/pip')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          Create Performance Improvement Plan
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Important:</strong> A Performance Improvement Plan (PIP) is a formal process to help employees improve their performance. 
        The employee will be notified and required to acknowledge this plan.
      </Alert>

      {/* Employee Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Employee Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={employees.filter(e => e._id)}
              getOptionLabel={(option) => `${option.staff_name || option.name || 'Unknown'} (${option.emp_id || option.employeeCode || 'N/A'})`}
              value={employees.find(e => e._id === formData.employeeId) || null}
              onChange={(e, value) => setFormData(prev => ({ ...prev, employeeId: value?._id || '', performanceReviewId: '' }))}
              renderInput={(params) => (
                <TextField {...params} label="Employee *" required />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={reviews}
              getOptionLabel={(option) => `Review: ${new Date(option.reviewDate).toLocaleDateString()} - ${option.rating}`}
              value={reviews.find(r => r._id === formData.performanceReviewId) || null}
              onChange={(e, value) => setFormData(prev => ({ ...prev, performanceReviewId: value?._id || '' }))}
              renderInput={(params) => (
                <TextField {...params} label="Related Performance Review (Optional)" />
              )}
              disabled={!formData.employeeId}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* PIP Duration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          PIP Duration
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Start Date *"
              type="date"
              value={formData.pipStartDate}
              onChange={(e) => setFormData(prev => ({ ...prev, pipStartDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Duration (Days) *"
              type="number"
              value={formData.pipDuration}
              onChange={(e) => setFormData(prev => ({ ...prev, pipDuration: parseInt(e.target.value) || 30 }))}
              required
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.pipEndDate}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Review Frequency *"
              value={formData.reviewFrequency}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewFrequency: e.target.value }))}
              required
            >
              {reviewFrequencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Performance Issues */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Performance Issues *
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          List the specific performance issues that need to be addressed
        </Typography>
        {formData.performanceIssues.map((issue, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={issue}
              onChange={(e) => handleArrayFieldChange('performanceIssues', index, e.target.value)}
              placeholder="Describe the performance issue..."
            />
            {formData.performanceIssues.length > 1 && (
              <IconButton
                color="error"
                onClick={() => handleRemoveArrayField('performanceIssues', index)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddArrayField('performanceIssues')}
          size="small"
        >
          Add Issue
        </Button>
      </Paper>

      {/* Objectives */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Improvement Objectives *
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Define clear, measurable objectives for improvement
        </Typography>
        {formData.objectives.map((objective, index) => (
          <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Objective *"
                  value={objective.objective}
                  onChange={(e) => {
                    const newObjectives = [...formData.objectives];
                    newObjectives[index].objective = e.target.value;
                    setFormData(prev => ({ ...prev, objectives: newObjectives }));
                  }}
                  placeholder="Define an improvement objective..."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Target Value"
                  value={objective.targetValue}
                  onChange={(e) => {
                    const newObjectives = [...formData.objectives];
                    newObjectives[index].targetValue = e.target.value;
                    setFormData(prev => ({ ...prev, objectives: newObjectives }));
                  }}
                  placeholder="e.g., 90% completion, 5 tasks"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Target Date"
                  value={objective.targetDate}
                  onChange={(e) => {
                    const newObjectives = [...formData.objectives];
                    newObjectives[index].targetDate = e.target.value;
                    setFormData(prev => ({ ...prev, objectives: newObjectives }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: formData.pipEndDate || undefined
                  }}
                  helperText={formData.pipEndDate ? `Must be on or before PIP end date: ${new Date(formData.pipEndDate).toLocaleDateString()}` : ''}
                />
              </Grid>
              {formData.objectives.length > 1 && (
                <Grid item xs={12}>
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleRemoveArrayField('objectives', index)}
                    size="small"
                  >
                    Remove Objective
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddArrayField('objectives')}
          size="small"
        >
          Add Objective
        </Button>
      </Paper>

      {/* Support Provided */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Support & Resources
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          List the support, training, or resources that will be provided
        </Typography>
        {formData.supportProvided.map((support, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              value={support}
              onChange={(e) => handleArrayFieldChange('supportProvided', index, e.target.value)}
              placeholder="Describe support or resources..."
            />
            {formData.supportProvided.length > 1 && (
              <IconButton
                color="error"
                onClick={() => handleRemoveArrayField('supportProvided', index)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddArrayField('supportProvided')}
          size="small"
        >
          Add Support
        </Button>
      </Paper>

      {/* Manager Comments */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manager Comments
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={formData.managerComments}
          onChange={(e) => setFormData(prev => ({ ...prev, managerComments: e.target.value }))}
          placeholder="Additional comments or context..."
        />
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/userdashboard/staff/hrm/performance/pip')}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create PIP'}
        </Button>
      </Box>
    </Box>
  );
};

export default PIPCreateForm;
